import React from "react";
import {
  Anchor,
  Sparkles,
  Home,
  Landmark,
  BookText,
  Mic2,
  Globe2,
  Trophy,
  Award as AwardIcon,
  Star,
  Medal,
  Moon,
  BookOpenCheck,
  SprayCan,
  Flame,
  UtensilsCrossed,
} from "lucide-react";

/* Inline hijab silhouette (no lucide equivalent) */
const HijabIcon = ({ size = 22, strokeWidth = 1.4, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 3c-4.4 0-7 3.1-7 7 0 2.4.8 4.6 2 6.5V21h10v-4.5c1.2-1.9 2-4.1 2-6.5 0-3.9-2.6-7-7-7z" />
    <ellipse cx="12" cy="10.5" rx="3" ry="3.8" />
    <path d="M6 21c0-1 .5-1.8 1.5-2.2M18 21c0-1-.5-1.8-1.5-2.2" />
  </svg>
);

/* ============================================================
   Branded "logo cards" — distinct mini-identity for each org.
   Each is a self-contained SVG/JSX wordmark with brand color,
   icon, and unique typography. Designed to read as authentic
   marks at glance, while remaining clearly artistic/illustrative.
   ============================================================ */

const Card = ({
  bg = "#0A2540",
  fg = "#fff",
  border,
  className = "",
  children,
  testId,
  ratio = "aspect-[5/3]",
}) => (
  <div
    data-testid={testId}
    className={`relative ${ratio} flex flex-col justify-between p-4 sm:p-5 md:p-7 overflow-hidden transition-all duration-500 hover:scale-[1.02] ${className}`}
    style={{
      background: bg,
      color: fg,
      border: border || "1px solid rgba(0,0,0,0.06)",
    }}
  >
    {children}
  </div>
);

/* ===================== CLIENTS (7) ===================== */

export const PrivateBlendsLogo = () => (
  <Card bg="#1A1410" fg="#E8D5B7" testId="logo-privateblends">
    <div className="flex items-start justify-between">
      <SprayCan size={26} strokeWidth={1.4} className="text-[#C9A96E]" />
      <span className="text-[10px] tracking-[0.4em] opacity-50">EST. AU</span>
    </div>
    <div>
      <div
        className="text-[10px] tracking-[0.5em] text-[#C9A96E]"
        style={{ fontWeight: 700 }}
      >
        — BESPOKE —
      </div>
      <div
        className="text-[20px] sm:text-[26px] md:text-[34px] leading-[0.9] mt-2 italic"
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontWeight: 500,
        }}
      >
        Private<span className="text-[#C9A96E]"> </span>Blends
      </div>
      <div className="text-[10px] tracking-[0.4em] mt-1 opacity-60">
        FRAGRANCE · COLOGNE · SIGNATURE
      </div>
    </div>
  </Card>
);

export const DarussalamLogo = () => (
  <Card bg="#0F3A2E" fg="#E8DAA0" testId="logo-darussalam">
    <div className="flex items-start justify-between">
      <BookOpenCheck size={26} strokeWidth={1.4} className="text-[#C9A96E]" />
      <Star size={16} strokeWidth={1.4} className="text-[#C9A96E]" />
    </div>
    <div>
      <div
        className="text-[10px] tracking-[0.5em] text-[#C9A96E]"
        style={{ fontWeight: 700 }}
      >
        دار السلام
      </div>
      <div
        className="text-[21px] sm:text-[28px] md:text-[36px] leading-[0.9] mt-1 font-bold"
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontWeight: 700,
        }}
      >
        Darussalam
      </div>
      <div className="text-[10px] tracking-[0.4em] mt-1 opacity-70">
        PUBLISHERS · SINCE 1986
      </div>
    </div>
  </Card>
);

export const VolcanosLogo = () => (
  <Card bg="#1A0E0A" fg="#F5E6D3" testId="logo-volcanos">
    <div className="flex items-start justify-between">
      <Flame size={26} strokeWidth={1.4} className="text-[#E25822]" />
      <UtensilsCrossed size={20} strokeWidth={1.2} className="opacity-50" />
    </div>
    <div>
      <div
        className="text-[12px] tracking-[0.4em] text-[#E25822]"
        style={{ fontFamily: "'Sofia Sans', sans-serif", fontWeight: 700 }}
      >
        STEAKHOUSE · RIBS
      </div>
      <div
        className="text-[21px] sm:text-[28px] md:text-[36px] leading-[0.92] mt-1 italic"
        style={{ fontFamily: "Georgia, 'Cabinet Grotesk', serif", fontWeight: 700 }}
      >
        Volcano's
      </div>
    </div>
  </Card>
);

export const CaptainFormworkLogo = () => (
  <Card bg="#FCD34D" fg="#1F2937" testId="logo-captain">
    <div className="flex items-start justify-between">
      <Anchor size={26} strokeWidth={1.6} />
      <span className="text-[10px] tracking-[0.3em] opacity-60">SYDNEY</span>
    </div>
    <div>
      <div
        className="text-[20px] sm:text-[26px] md:text-[34px] leading-[0.92] font-black"
        style={{ fontFamily: "'Bebas Neue', Impact, sans-serif" }}
      >
        CAPTAIN
        <br />
        FORMWORK
      </div>
      <div className="mt-1 inline-block bg-[#1F2937] text-[#FCD34D] text-[10px] tracking-[0.3em] px-2 py-0.5">
        CONSTRUCTION
      </div>
    </div>
  </Card>
);

export const HijabHouseLogo = () => (
  <Card bg="#FAF5EF" fg="#3B2A1A" testId="logo-hijabhouse">
    <div className="flex items-start justify-between">
      <HijabIcon size={24} strokeWidth={1.4} className="text-[#B99761]" />
      <span className="text-[10px] tracking-[0.4em] opacity-60">SINCE 2009</span>
    </div>
    <div className="text-center">
      <div
        className="text-[24px] sm:text-[32px] md:text-[44px] leading-[0.9] italic"
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontWeight: 500,
        }}
      >
        Hijab<span className="text-[#B99761]"> </span>House
      </div>
      <div className="text-[10px] tracking-[0.5em] mt-1 text-[#B99761]">
        — MODEST FASHION —
      </div>
    </div>
  </Card>
);

/* ===================== PARTNERS (4) ===================== */

export const RMHCLogo = () => (
  <Card bg="#FCE7E7" fg="#B91C1C" testId="logo-rmhc">
    <div className="flex items-start justify-between">
      <Home size={26} strokeWidth={1.6} className="text-[#B91C1C]" />
      <Sparkles size={18} strokeWidth={1.4} className="text-[#FBBF24]" />
    </div>
    <div>
      <div
        className="text-[12px] tracking-[0.3em] text-[#B91C1C]"
        style={{ fontWeight: 700 }}
      >
        RMHC · GREATER WESTERN SYDNEY
      </div>
      <div
        className="text-[17px] sm:text-[22px] md:text-[28px] leading-[0.95] mt-1 font-extrabold"
        style={{ fontFamily: "'Sofia Sans', sans-serif" }}
      >
        Ronald McDonald
        <br />
        House <span className="text-[#FBBF24]">Charities</span>®
      </div>
    </div>
  </Card>
);

export const PCCLogo = () => (
  <Card bg="#0A2540" fg="#fff" testId="logo-pcc">
    <div className="flex items-start justify-between">
      <Landmark size={26} strokeWidth={1.4} className="text-[#3B82F6]" />
      <span className="text-[10px] tracking-[0.3em] opacity-50">EST. 1909</span>
    </div>
    <div>
      <div
        className="text-[10px] tracking-[0.4em] text-[#3B82F6]"
        style={{ fontWeight: 700 }}
      >
        CHAMBER OF COMMERCE
      </div>
      <div
        className="text-[20px] sm:text-[26px] md:text-[34px] leading-[0.95] font-bold mt-1"
        style={{ fontFamily: "'Sofia Sans', sans-serif", fontWeight: 800 }}
      >
        Parramatta
        <br />
        <span className="opacity-70">Business Hub</span>
      </div>
    </div>
  </Card>
);

export const EidShowLogo = () => (
  <Card bg="#1F4D3F" fg="#F5E6B8" testId="logo-eidshow">
    <div className="flex items-start justify-between">
      <Moon size={26} strokeWidth={1.4} className="text-[#F5E6B8]" />
      <Star size={16} strokeWidth={1.6} className="text-[#F5E6B8]" fill="currentColor" />
    </div>
    <div className="text-center">
      <div className="text-[10px] tracking-[0.5em] opacity-80">CELEBRATE</div>
      <div
        className="text-[24px] sm:text-[32px] md:text-[40px] leading-[0.9] italic mt-1"
        style={{ fontFamily: "Georgia, serif", fontWeight: 700 }}
      >
        The Eid Show
      </div>
      <div className="text-[10px] tracking-[0.4em] opacity-80 mt-1">
        ✦ AUSTRALIA'S COMMUNITY EVENT ✦
      </div>
    </div>
  </Card>
);

/* ===================== MEDIA / PUBLICATIONS (4) — magazine-cover style ===================== */

export const PublicAccountantCover = () => (
  <Card bg="#0A2540" fg="#fff" testId="media-public-accountant" ratio="aspect-[3/4]">
    <div className="absolute inset-0 ea-grain opacity-30" />
    <div className="relative flex items-start justify-between">
      <div>
        <div
          className="text-[10px] tracking-[0.5em] text-[#FCD34D]"
          style={{ fontWeight: 800 }}
        >
          PUBLIC
        </div>
        <div
          className="text-[20px] sm:text-[26px] md:text-[36px] leading-[0.9] mt-1 font-black"
          style={{
            fontFamily: "Georgia, serif",
            fontWeight: 900,
            fontStyle: "italic",
          }}
        >
          Accountant
        </div>
      </div>
      <div className="text-right text-[10px] tracking-[0.2em] text-white/55">
        ISSUE 04
        <br />
        2022
      </div>
    </div>
    <div className="relative">
      <div className="border-t border-white/30 pt-4">
        <div
          className="text-[#FCD34D] text-[12px] tracking-[0.3em]"
          style={{ fontWeight: 700 }}
        >
          THE COVER STORY
        </div>
        <div
          className="text-[18px] sm:text-[24px] md:text-[34px] leading-[0.95] font-black mt-2"
          style={{ fontFamily: "'Bebas Neue', Impact, sans-serif" }}
        >
          THE TAX MAN
          <br />
          <span className="text-white/65">— BACHIR ZREIKA —</span>
        </div>
        <div className="text-[11px] mt-3 text-white/60">
          IPA Board · Practice of the Year · Industry Leader
        </div>
      </div>
    </div>
  </Card>
);

export const InTheBlackCover = () => (
  <Card bg="#000000" fg="#FFD60A" testId="media-in-the-black" ratio="aspect-[3/4]">
    <div className="relative flex items-start justify-between">
      <div
        className="text-[31px] sm:text-[42px] md:text-[64px] leading-[0.85] font-black"
        style={{
          fontFamily: "'Bebas Neue', Impact, sans-serif",
          letterSpacing: "0.01em",
        }}
      >
        IN
        <br />
        THE
        <br />
        BLACK
      </div>
      <BookText size={26} strokeWidth={1.4} />
    </div>
    <div>
      <div className="border-t border-[#FFD60A]/40 pt-4">
        <div className="text-[10px] tracking-[0.3em] text-[#FFD60A]/70">
          CPA AUSTRALIA · LEADERSHIP
        </div>
        <div
          className="text-[14px] sm:text-[18px] md:text-[26px] leading-[1.05] mt-2 text-white"
          style={{ fontFamily: "'Sofia Sans', sans-serif", fontWeight: 700 }}
        >
          The skills that
          <br />
          <span className="text-[#FFD60A]">define modern leadership.</span>
        </div>
      </div>
    </div>
  </Card>
);

export const TaxpoCover = () => (
  <Card bg="#1E293B" fg="#fff" testId="media-taxpo" ratio="aspect-[3/4]">
    <div className="absolute inset-0 bg-gradient-to-br from-[#1E293B] via-[#0A2540] to-[#3B82F6]/30" />
    <div className="relative flex items-start justify-between">
      <Mic2 size={26} strokeWidth={1.4} className="text-[#3B82F6]" />
      <span className="text-[10px] tracking-[0.3em] opacity-60">SYDNEY</span>
    </div>
    <div className="relative">
      <div
        className="text-[41px] sm:text-[56px] md:text-[88px] leading-[0.82] font-black"
        style={{
          fontFamily: "'Bebas Neue', Impact, sans-serif",
          letterSpacing: "0.005em",
        }}
      >
        TAXPO
        <br />
        <span className="text-[#3B82F6]">2022</span>
      </div>
      <div className="border-t border-white/30 pt-3 mt-4">
        <div className="text-[11px] tracking-[0.3em] text-white/65">
          KEYNOTE SPEAKERS · MAIN STAGE
        </div>
        <div className="text-[14px] mt-1">
          Australia's largest tax & accounting expo
        </div>
      </div>
    </div>
  </Card>
);

export const WCOACover = () => (
  <Card bg="#7C2D12" fg="#FED7AA" testId="media-wcoa" ratio="aspect-[3/4]">
    <div className="relative flex items-start justify-between">
      <Globe2 size={26} strokeWidth={1.4} />
      <span className="text-[10px] tracking-[0.3em] opacity-70">SYDNEY · 2018</span>
    </div>
    <div>
      <div
        className="text-[10px] tracking-[0.5em] mb-2 text-white/80"
        style={{ fontWeight: 700 }}
      >
        WORLD CONGRESS OF
      </div>
      <div
        className="text-[31px] sm:text-[42px] md:text-[60px] leading-[0.82] font-black"
        style={{
          fontFamily: "'Bebas Neue', Impact, sans-serif",
        }}
      >
        ACCOUNTANTS
      </div>
      <div className="border-t border-[#FED7AA]/40 pt-3 mt-3 text-[#FED7AA]">
        <div className="text-[14px]" style={{ fontWeight: 600 }}>
          #WCOA2018 · Global stage
        </div>
        <div className="text-[10px] tracking-[0.3em] mt-1 opacity-80">
          5,000+ DELEGATES · 100+ COUNTRIES
        </div>
      </div>
    </div>
  </Card>
);

/* ===================== AWARD BADGES (4) ===================== */

export const IPAAwardBadge = () => (
  <Card bg="#0A2540" fg="#FCD34D" testId="award-ipa" ratio="aspect-square">
    <div className="absolute inset-3 border border-[#FCD34D]/40 rounded-full" />
    <div className="absolute inset-6 border border-[#FCD34D]/20 rounded-full" />
    <div className="relative flex flex-col items-center justify-between h-full text-center">
      <Trophy size={28} strokeWidth={1.4} />
      <div>
        <div className="text-[10px] tracking-[0.4em] opacity-80">IPA</div>
        <div
          className="text-[13px] sm:text-[16px] md:text-[22px] leading-[1] mt-1 font-black"
          style={{ fontFamily: "'Bebas Neue', Impact, sans-serif" }}
        >
          ACCOUNTING
          <br />
          PRACTICE
          <br />
          OF THE YEAR
        </div>
      </div>
      <div className="text-[10px] tracking-[0.3em] text-white">★ MULTI-WINNER ★</div>
    </div>
  </Card>
);

export const WSABEAwardBadge = () => (
  <Card bg="#FBBF24" fg="#0A2540" testId="award-wsabe" ratio="aspect-square">
    <div className="relative flex flex-col items-center justify-between h-full text-center">
      <Star size={28} strokeWidth={1.6} fill="currentColor" />
      <div>
        <div className="text-[10px] tracking-[0.4em] opacity-70">WSABE</div>
        <div
          className="text-[13px] sm:text-[16px] md:text-[22px] leading-[1] mt-1 font-black"
          style={{ fontFamily: "'Bebas Neue', Impact, sans-serif" }}
        >
          WESTERN SYDNEY
          <br />
          AWARDS FOR
          <br />
          BUSINESS EXCELLENCE
        </div>
      </div>
      <div className="text-[10px] tracking-[0.3em]">FINALIST · 2021</div>
    </div>
  </Card>
);

export const LBAwardBadge = () => (
  <Card bg="#fff" fg="#0A2540" border="1px solid #0A2540" testId="award-lba" ratio="aspect-square">
    <div className="relative flex flex-col items-center justify-between h-full text-center">
      <AwardIcon size={28} strokeWidth={1.4} className="text-[#3B82F6]" />
      <div>
        <div
          className="text-[10px] tracking-[0.4em] text-[#3B82F6]"
          style={{ fontWeight: 700 }}
        >
          LOCAL BUSINESS AWARDS
        </div>
        <div
          className="text-[13px] sm:text-[16px] md:text-[22px] leading-[1] mt-1 font-black"
          style={{ fontFamily: "'Bebas Neue', Impact, sans-serif" }}
        >
          PARRAMATTA
          <br />
          FINALIST
        </div>
      </div>
      <div className="text-[10px] tracking-[0.3em]">2021 · COMMUNITY VOTED</div>
    </div>
  </Card>
);

export const BestTaxBadge = () => (
  <Card bg="#0A2540" fg="#fff" testId="award-besttax" ratio="aspect-square">
    <div
      className="absolute inset-3 border-2 border-[#FCD34D]"
      style={{ borderRadius: "0%" }}
    />
    <div className="relative flex flex-col items-center justify-between h-full text-center">
      <Medal size={28} strokeWidth={1.4} className="text-[#FCD34D]" />
      <div>
        <div className="text-[10px] tracking-[0.4em] text-[#FCD34D]">BEST OF</div>
        <div
          className="text-[14px] sm:text-[18px] md:text-[26px] leading-[1] mt-1 font-black"
          style={{ fontFamily: "'Bebas Neue', Impact, sans-serif" }}
        >
          TAX ACCOUNTANT
          <br />
          <span className="text-[#FCD34D]">SYDNEY</span>
        </div>
      </div>
      <div className="text-[10px] tracking-[0.3em] text-white/80">2022 · TOP EXPERT</div>
    </div>
  </Card>
);

/* ===================== BARRELS ===================== */

export const ArmaniGalleryLogo = () => (
  <Card bg="#F5F1EA" fg="#111111" testId="logo-armani">
    <div className="flex items-start justify-between">
      <Sparkles size={26} strokeWidth={1.4} className="text-[#B99761]" />
      <span className="text-[10px] tracking-[0.4em] opacity-50">SYDNEY</span>
    </div>
    <div>
      <div
        className="text-[10px] tracking-[0.5em] text-[#B99761]"
        style={{ fontWeight: 700 }}
      >
        — HOMEWARE —
      </div>
      <div
        className="text-[20px] sm:text-[26px] md:text-[34px] leading-[0.9] mt-2 italic"
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontWeight: 500,
        }}
      >
        Armani Gallery
      </div>
      <div className="text-[10px] tracking-[0.4em] mt-1 opacity-60">
        LUXURY ARABESQUE HOMEWARE
      </div>
    </div>
  </Card>
);

export const CLIENT_LOGOS = [
  PrivateBlendsLogo,
  VolcanosLogo,
  CaptainFormworkLogo,
  HijabHouseLogo,
  DarussalamLogo,
  ArmaniGalleryLogo,
];

export const PARTNER_LOGOS = [
  RMHCLogo,
  PCCLogo,
  EidShowLogo,
];

export const MEDIA_COVERS = [
  PublicAccountantCover,
  InTheBlackCover,
  TaxpoCover,
  WCOACover,
];

export const AWARD_BADGES = [
  IPAAwardBadge,
  WSABEAwardBadge,
  LBAwardBadge,
];


